/**
 * Errors list component
 * Display and manage job errors
 */

import { useState } from 'react';
import { Card, Table, Typography, Tag, Button, Space, Tooltip, Empty, Modal, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import type { GridCell } from '@/types/grid';
import { formatDate, formatRelativeTime } from '@/utils/formatters';

const { Text, Paragraph, Title } = Typography;

interface ErrorInfo {
  id: string;
  cellId: string;
  cellIndex?: number;
  error: string;
  timestamp: string;
  retryCount?: number;
  details?: Record<string, unknown>;
}

interface ErrorsListProps {
  errors: ErrorInfo[];
  onRetryCell?: (cellId: string) => void;
  onRetryAll?: () => void;
  loading?: boolean;
}

export function ErrorsList({
  errors,
  onRetryCell,
  onRetryAll,
  loading,
}: ErrorsListProps) {
  const [selectedError, setSelectedError] = useState<ErrorInfo | null>(null);

  const getErrorType = (error: string): { type: string; color: string; suggestion?: string } => {
    const errorLower = error.toLowerCase();

    if (errorLower.includes('timeout')) {
      return {
        type: 'Timeout',
        color: 'orange',
        suggestion: 'The page took too long to load. Try increasing timeout settings or reducing concurrency.',
      };
    }
    if (errorLower.includes('rate limit') || errorLower.includes('429')) {
      return {
        type: 'Rate Limited',
        color: 'gold',
        suggestion: 'Google is rate limiting requests. Increase delays or use rotating proxies.',
      };
    }
    if (errorLower.includes('captcha')) {
      return {
        type: 'Captcha',
        color: 'red',
        suggestion: 'A captcha was triggered. Enable anti-detection measures or use residential proxies.',
      };
    }
    if (errorLower.includes('network') || errorLower.includes('connection')) {
      return {
        type: 'Network',
        color: 'volcano',
        suggestion: 'Network connectivity issue. Check proxy health and internet connection.',
      };
    }
    if (errorLower.includes('proxy')) {
      return {
        type: 'Proxy',
        color: 'magenta',
        suggestion: 'Proxy error occurred. Check proxy credentials and availability.',
      };
    }
    if (errorLower.includes('selector') || errorLower.includes('element')) {
      return {
        type: 'Parsing',
        color: 'purple',
        suggestion: 'Page structure may have changed. The scraper may need updates.',
      };
    }

    return {
      type: 'Unknown',
      color: 'default',
      suggestion: 'An unexpected error occurred. Check the error details for more information.',
    };
  };

  const columns: ColumnsType<ErrorInfo> = [
    {
      title: 'Cell',
      key: 'cell',
      width: 80,
      render: (_, record) => (
        <Text strong>#{record.cellIndex !== undefined ? record.cellIndex + 1 : record.cellId}</Text>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      width: 100,
      render: (_, record) => {
        const { type, color } = getErrorType(record.error);
        return <Tag color={color}>{type}</Tag>;
      },
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
      ellipsis: true,
      render: (error: string) => (
        <Tooltip title={error}>
          <Text style={{ maxWidth: 300 }} ellipsis>
            {error}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 100,
      render: (timestamp: string) => (
        <Tooltip title={formatDate(timestamp, 'full')}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatRelativeTime(timestamp)}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Retries',
      dataIndex: 'retryCount',
      key: 'retryCount',
      width: 70,
      align: 'center',
      render: (count: number = 0) => (
        <Text type={count > 0 ? 'warning' : 'secondary'}>{count}</Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="View details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => setSelectedError(record)}
            />
          </Tooltip>
          {onRetryCell && (
            <Tooltip title="Retry this cell">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => onRetryCell(record.cellId)}
                loading={loading}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  if (errors.length === 0) {
    return (
      <Card title="Errors">
        <Empty
          description="No errors"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <>
      <Card
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Errors ({errors.length})</span>
          </Space>
        }
        extra={
          onRetryAll && errors.length > 0 && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={onRetryAll}
              loading={loading}
            >
              Retry All Failed
            </Button>
          )
        }
      >
        <Table
          columns={columns}
          dataSource={errors}
          rowKey="id"
          size="small"
          pagination={errors.length > 10 ? { pageSize: 10 } : false}
          loading={loading}
        />
      </Card>

      {/* Error detail modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            Error Details
          </Space>
        }
        open={!!selectedError}
        onCancel={() => setSelectedError(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedError(null)}>
            Close
          </Button>,
          onRetryCell && selectedError && (
            <Button
              key="retry"
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => {
                onRetryCell(selectedError.cellId);
                setSelectedError(null);
              }}
              loading={loading}
            >
              Retry Cell
            </Button>
          ),
        ]}
        width={600}
      >
        {selectedError && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Text type="secondary">Cell</Text>
              <div>
                <Text strong>
                  #{selectedError.cellIndex !== undefined ? selectedError.cellIndex + 1 : selectedError.cellId}
                </Text>
              </div>
            </div>

            <div>
              <Text type="secondary">Error Type</Text>
              <div>
                <Tag color={getErrorType(selectedError.error).color}>
                  {getErrorType(selectedError.error).type}
                </Tag>
              </div>
            </div>

            <div>
              <Text type="secondary">Error Message</Text>
              <Paragraph
                style={{
                  padding: 12,
                  background: 'var(--color-bg-spotlight)',
                  borderRadius: 4,
                  marginBottom: 0,
                }}
              >
                {selectedError.error}
              </Paragraph>
            </div>

            <Alert
              icon={<BulbOutlined />}
              message="Suggestion"
              description={getErrorType(selectedError.error).suggestion}
              type="info"
              showIcon
            />

            <div>
              <Text type="secondary">Timestamp</Text>
              <div>
                <Text>{formatDate(selectedError.timestamp, 'full')}</Text>
              </div>
            </div>

            {selectedError.retryCount !== undefined && selectedError.retryCount > 0 && (
              <div>
                <Text type="secondary">Retry Attempts</Text>
                <div>
                  <Text type="warning">{selectedError.retryCount} attempts</Text>
                </div>
              </div>
            )}

            {selectedError.details && (
              <div>
                <Text type="secondary">Additional Details</Text>
                <pre
                  style={{
                    padding: 12,
                    background: 'var(--color-bg-spotlight)',
                    borderRadius: 4,
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: 200,
                  }}
                >
                  {JSON.stringify(selectedError.details, null, 2)}
                </pre>
              </div>
            )}
          </Space>
        )}
      </Modal>
    </>
  );
}

/**
 * Compact error summary
 */
export function ErrorsSummary({
  errorCount,
  onViewAll,
}: {
  errorCount: number;
  onViewAll?: () => void;
}) {
  if (errorCount === 0) return null;

  return (
    <Alert
      type="error"
      message={
        <Space>
          <span>{errorCount} cells failed</span>
          {onViewAll && (
            <Button type="link" size="small" onClick={onViewAll} style={{ padding: 0 }}>
              View errors
            </Button>
          )}
        </Space>
      }
      showIcon
      style={{ marginBottom: 16 }}
    />
  );
}

export default ErrorsList;
